import classNames from "classnames";
import { IPaneSwap, usePaneSwap } from "../../../shared/hooks/usePaneSwap";
import "./kuika-status-glyph.scss";

/**
 * A status mark whose meaning is carried by its SHAPE — a dashed ring for not-started, a
 * half-filled disc for in-progress, a filled disc with a check for done — and only tinted by
 * the caller's colour.
 *
 * It replaced plain coloured dots on two surfaces (the Kanban board's column headers and the
 * Helpdesk board's), where the category was encoded in colour ALONE and the column NAME was
 * tinted from the same scale — so a viewer who cannot separate those hues had no second channel
 * anywhere in the header (WCAG 1.4.1). Shape gives them one, and it survives a greyscale
 * screenshot, a projector and a dark theme without a second palette.
 *
 * Kept domain-free on purpose: the two surfaces have different status vocabularies (todo /
 * in_progress / done vs. open / pending / solved) and different colour maps, so each one maps
 * its own statuses onto these three shapes rather than this component learning either.
 */
export type KuikaStatusGlyphVariant = "not-started" | "in-progress" | "done";

export interface IKuikaStatusGlyphProps {
  variant: KuikaStatusGlyphVariant;
  /** Any CSS colour — pass the caller's own status token. */
  color: string;
  /** Announced to assistive tech; the glyph is an `img` role, not decoration. */
  label: string;
  size?: number;
  className?: string;
  /**
   * Animates a change of status in place, the way KuikaBadge's `animateChanges` does: the colour
   * fades over and the old shape leaves with a short blur while the new one arrives, inside the
   * same svg (one `img`, one test id; the leaving shape is `aria-hidden`). Off by default — a glyph
   * re-used for another row or column must not pretend its status just changed. Turn it on where
   * the same item's status really changes in front of the user. The first render never animates;
   * under reduced motion the swap is a plain crossfade.
   */
  animateChanges?: boolean;
}

const renderShape = (variant: KuikaStatusGlyphVariant): JSX.Element => {
  if (variant === "not-started") {
    return <circle cx="7" cy="7" r="5.25" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2.6 2.2" />;
  }

  if (variant === "in-progress") {
    return (
      <>
        <circle cx="7" cy="7" r="5.25" stroke="currentColor" strokeWidth="1.5" />
        {/* The right half, drawn as an arc back to centre — "half done" without a second colour. */}
        <path d="M7 1.75 A5.25 5.25 0 0 1 7 12.25 Z" fill="currentColor" />
      </>
    );
  }

  return (
    <>
      <circle cx="7" cy="7" r="6" fill="currentColor" />
      {/* The tick is punched in the surface colour so it reads on any fill the caller passes. */}
      <path
        d="M4.4 7.2 L6.2 9 L9.7 5.4"
        stroke="var(--dark-0)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  );
};

const describeNothing = (): null => null;

const KuikaStatusGlyph = ({
  variant,
  color,
  label,
  size = 14,
  className,
  animateChanges = false
}: IKuikaStatusGlyphProps): JSX.Element => {
  // Without animateChanges there is never a leaver and no entrance: the shape changes at once.
  const swap: IPaneSwap<null> = usePaneSwap<null>(variant, animateChanges, describeNothing);

  return (
    <svg
      className={classNames("kuika-status-glyph", { "kuika-status-glyph--animated": animateChanges }, className)}
      width={size}
      height={size}
      viewBox="0 0 14 14"
      fill="none"
      role="img"
      aria-label={label}
      style={{ color }}
      data-testid="kuikaStatusGlyph"
    >
      {swap.leaverKey !== null && (
        <g
          key={swap.leaverKey}
          className="kuika-status-glyph__shape kuika-status-glyph__shape--out"
          aria-hidden="true"
          onAnimationEnd={swap.onLeaverAnimationEnd}
        >
          {renderShape(swap.leaverKey as KuikaStatusGlyphVariant)}
        </g>
      )}
      <g
        key={variant}
        className={classNames("kuika-status-glyph__shape", {
          "kuika-status-glyph__shape--in": animateChanges && swap.hasSwapped
        })}
      >
        {renderShape(variant)}
      </g>
    </svg>
  );
};

export default KuikaStatusGlyph;
